/**
 * Notifications domain — React Query hooks.
 *
 * The list endpoint returns the 5 most recent notifications as a flat
 * array, but drf-spectacular wraps it in DRF auto-pagination. We hand-
 * write the list hook (and the unread-count hook for `refetchInterval`
 * polling) so consumers see the actual runtime shapes.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query'

import { listNotifications } from '../generated/clients/listNotifications'
import { getUnreadNotificationsCount as getUnreadNotificationsCountClient } from '../generated/clients/getUnreadNotificationsCount'
import { markAllNotificationsAsRead } from '../generated/clients/markAllNotificationsAsRead'
import { markNotificationsAsRead } from '../generated/clients/markNotificationsAsRead'
import { deleteNotification } from '../generated/clients/deleteNotification'
import { updateAllNotificationPreferencesByKind } from '../generated/clients/updateAllNotificationPreferencesByKind'
import { updateNotificationPreference } from '../generated/clients/updateNotificationPreference'
import {
  getEffectiveNotificationPreferencesByKindQueryKey,
  getUnreadNotificationsCountQueryKey,
  listNotificationsQueryKey,
  useGetEffectiveNotificationPreferencesByKind,
} from '../generated/hooks/notificationsController'
import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

import type { ListNotificationsQueryParams } from '../generated/types/ListNotifications'
import type { Notification } from '../generated/types/Notification'
import type { GetUnreadNotificationsCountQueryResponse } from '../generated/types/GetUnreadNotificationsCount'
import type { MarkNotificationsAsReadMutationRequest } from '../generated/types/MarkNotificationsAsRead'
import type { DeleteNotificationPathParams } from '../generated/types/DeleteNotification'
import type {
  UpdateAllNotificationPreferencesByKindMutationRequest,
  UpdateAllNotificationPreferencesByKindPathParams,
} from '../generated/types/UpdateAllNotificationPreferencesByKind'
import type { UpdateNotificationPreferenceMutationRequest } from '../generated/types/UpdateNotificationPreference'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useGetEffectiveNotificationPreferencesByKind,
  getEffectiveNotificationPreferencesByKindQueryKey,
  listNotificationsQueryKey,
  getUnreadNotificationsCountQueryKey,
}

// ── Generated query/mutation parameter & response type re-exports ──

export type {
  ListNotificationsQueryParams,
  ListNotificationsQueryResponse,
} from '../generated/types/ListNotifications'
export type { GetUnreadNotificationsCountQueryResponse } from '../generated/types/GetUnreadNotificationsCount'
export type {
  GetEffectiveNotificationPreferencesByKindQueryResponse,
  GetEffectiveNotificationPreferencesByKindPathParams,
} from '../generated/types/GetEffectiveNotificationPreferencesByKind'
export type {
  MarkNotificationsAsReadMutationRequest,
  MarkNotificationsAsReadMutationResponse,
} from '../generated/types/MarkNotificationsAsRead'
export type { MarkAllNotificationsAsReadMutationResponse } from '../generated/types/MarkAllNotificationsAsRead'
export type {
  DeleteNotificationMutationResponse,
  DeleteNotificationPathParams,
} from '../generated/types/DeleteNotification'
export type {
  UpdateAllNotificationPreferencesByKindMutationRequest,
  UpdateAllNotificationPreferencesByKindMutationResponse,
  UpdateAllNotificationPreferencesByKindPathParams,
} from '../generated/types/UpdateAllNotificationPreferencesByKind'
export type {
  UpdateNotificationPreferenceMutationRequest,
  UpdateNotificationPreferenceMutationResponse,
} from '../generated/types/UpdateNotificationPreference'

// ── Hand-written query hooks ───────────────────────────────────────

/**
 * List the 5 most recent notifications for the current user.
 *
 * Hand-written wrapper because the view returns a flat
 * `Notification[]` (not paginated), but drf-spectacular wraps the
 * `ListModelMixin` response in DRF auto-pagination. The runtime is
 * always a plain array.
 */
export function useListNotifications(
  params?: ListNotificationsQueryParams,
  options: {
    query?: Omit<UseQueryOptions<Notification[], Error>, 'queryKey' | 'queryFn'>
  } = {}
) {
  const { query = {} } = options
  return useQuery<Notification[], Error>({
    queryKey: listNotificationsQueryKey(params),
    queryFn: async ({ signal }) =>
      (await listNotifications(params, {
        signal,
      })) as unknown as Notification[],
    ...query,
  })
}

/**
 * Poll the unread notifications count every 10 seconds.
 *
 * Hand-written so the hook can set `refetchInterval` (frontend concern,
 * not OpenAPI-spec concern) without forcing every consumer to wire
 * polling themselves.
 */
export function useGetUnreadNotificationsCount(
  options: {
    query?: Omit<
      UseQueryOptions<GetUnreadNotificationsCountQueryResponse, Error>,
      'queryKey' | 'queryFn'
    >
  } = {}
) {
  const { query = {} } = options
  return useQuery<GetUnreadNotificationsCountQueryResponse, Error>({
    queryKey: getUnreadNotificationsCountQueryKey(),
    queryFn: async ({ signal }) =>
      (await getUnreadNotificationsCountClient({
        signal,
      })) as unknown as GetUnreadNotificationsCountQueryResponse,
    refetchOnMount: true,
    refetchInterval: 10_000,
    ...query,
  })
}

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

export function useMarkAllNotificationsAsRead() {
  return useInvalidatingMutation(
    'markAllNotificationsAsRead',
    invalidationRegistry,
    {
      mutationFn: () => markAllNotificationsAsRead(),
    }
  )
}

export function useMarkNotificationsAsRead() {
  return useInvalidatingMutation(
    'markNotificationsAsRead',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: MarkNotificationsAsReadMutationRequest
      }) => markNotificationsAsRead(variables.data),
    }
  )
}

export function useDeleteNotification() {
  return useInvalidatingMutation('deleteNotification', invalidationRegistry, {
    mutationFn: (variables: { id: DeleteNotificationPathParams['id'] }) =>
      deleteNotification({ id: variables.id }),
  })
}

export function useUpdateAllNotificationPreferencesByKind() {
  return useInvalidatingMutation(
    'updateAllNotificationPreferencesByKind',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        kind_slug: UpdateAllNotificationPreferencesByKindPathParams['kind_slug']
        data: UpdateAllNotificationPreferencesByKindMutationRequest
      }) =>
        updateAllNotificationPreferencesByKind(
          { kind_slug: variables.kind_slug },
          variables.data
        ),
    }
  )
}

/**
 * Update a single preference toggle.
 *
 * The mutation variables carry an extra `kind_slug` (not part of the
 * request body) so the invalidation registry can target the right
 * `notifications-preferences` cache slice — the backend doesn't
 * include it in the URL or body, but the UI knows which kind tab the
 * user is on.
 */
export function useUpdateNotificationPreference() {
  return useInvalidatingMutation(
    'updateNotificationPreference',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        kind_slug?: string
        data: UpdateNotificationPreferenceMutationRequest
      }) => updateNotificationPreference(variables.data),
    }
  )
}
